<template>
  <sf-dialog v-model="dialog">
    <template #activator="{ props }">
      <sf-edit-button
        v-bind="props"
        :aria-label="$t('orderLimits.editOrderLimitUser', [getFullName(user)])"
      />
    </template>
    <v-card>
      <v-card-item>
        <v-card-title>
          <sf-heading>{{ getFullName(user) }}</sf-heading>
        </v-card-title>
      </v-card-item>
      <v-card-text>
        <v-row>
          <v-col class="d-flex align-center" cols="12">
            <sf-sub-heading class="mr-2">{{ $t('orderLimits.orderRight') }}</sf-sub-heading>
            <v-switch
              v-model="orderLimitUserPatch.submitOrder"
              class="mt-0 pt-0"
              color="green"
              hide-details
              inset
              :aria-label="$t('orderLimits.orderRight')"
            />
          </v-col>
        </v-row>
        <v-divider class="my-2" />
        <v-row no-gutters>
          <v-col class="mr-2" cols="12" sm="auto">
            <sf-sub-heading>{{ $t('orderLimits.orderLimit') }}</sf-sub-heading>
          </v-col>
          <v-col cols="12" sm="">
            <v-radio-group
              v-model="orderLimitUserPatch.orderLimitType"
              color="primary"
              class="mt-0"
              hide-details
              density="compact"
            >
              <v-radio :label="$t('orderLimits.defaultLimit')" :value="OrderLimitType.DEFAULT" />
              <v-radio :label="$t('orderLimits.unlimited')" :value="OrderLimitType.UNLIMITED" />
              <v-radio
                :value="OrderLimitType.USER"
                class="d-flex align-start individual-limit-radio"
              >
                <template #label>
                  <div class="d-flex align-start flex-grow-1">
                    <span class="mr-2">{{ $t('orderLimits.individual') }}</span>
                    <v-form v-model="valid" lazy-validation class="flex-grow-1">
                      <sf-text-field
                        v-show="orderLimitUserPatch.orderLimitType === OrderLimitType.USER"
                        v-model.number="orderLimitUserPatch.userOrderLimit"
                        :rules="[requiredRule, positiveNumberRule]"
                        class="input-text-right"
                        hide-details="auto"
                        hide-spin-buttons
                        min="0"
                        :suffix="getCurrencySymbol(portalSettings.currency)"
                        type="number"
                        autocomplete="off"
                        @keydown.enter.prevent="save"
                      />
                    </v-form>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </v-col>
        </v-row>
        <v-divider class="my-2" />
        <div>
          <sf-sub-heading>{{ $t('orderLimits.orderApprover') }}</sf-sub-heading>
          <order-limit-approvers-select v-model="userApprovers" :approvers="approvers" />
          <div class="mt-1">
            <sf-text v-if="userApprovers.length === 0">
              {{ $t('orderLimits.defaultApproversUsed') }}
            </sf-text>
          </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <sf-text-button color="grey" class="text-medium-emphasis" @click="closeDialog">
          {{ $t('cancel') }}
        </sf-text-button>
        <sf-text-button
          :disabled="!valid && orderLimitUserPatch.orderLimitType === OrderLimitType.USER"
          @click="save"
        >
          {{ $t('save') }}
        </sf-text-button>
      </v-card-actions>
    </v-card>
  </sf-dialog>
</template>

<script setup lang="ts">
import SfEditButton from '@/components/button/SfEditButton.vue'
import SfTextButton from '@/components/button/SfTextButton.vue'
import SfDialog from '@/components/dialog/SfDialog.vue'
import SfTextField from '@/components/input/SfTextField.vue'
import SfHeading from '@/components/text/SfHeading.vue'
import SfSubHeading from '@/components/text/SfSubHeading.vue'
import SfText from '@/components/text/SfText.vue'
import { getCurrencySymbol, getFullName } from '@/helpers'
import OrderLimitApproversSelect from '@/modules/orderLimits/components/OrderLimitApproversSelect.vue'
import { trackOrderLimitDialogue } from '@/modules/orderLimits/trackingHelpers'
import useOrderLimits from '@/modules/orderLimits/useOrderLimits'
import usePortalSettings from '@/modules/portalSettings/usePortalSettings'
import { positiveNumberRule, requiredRule } from '@/rules'
import { OrderLimitType, OrderLimitUser, OrderLimitUserPatch, ShortUser } from '@/types/index'
import { storeToRefs } from 'pinia'
import { PropType, ref, watch } from 'vue'

const componentProps = defineProps({
  user: {
    type: Object as PropType<OrderLimitUser>,
    required: true
  },
  approvers: {
    type: Array as PropType<ShortUser[]>,
    required: true
  }
})
const valid = ref(true)
const dialog = ref(false)
const userApprovers = ref<ShortUser[]>([...componentProps.user.approvers])
const { portalSettings } = storeToRefs(usePortalSettings())

const data = () => {
  return {
    submitOrder: componentProps.user.submitOrder,
    userOrderLimit: componentProps.user.userOrderLimit || 0,
    orderLimitType: componentProps.user.orderLimitType,
    approverIds: componentProps.user.approvers.map((approver) => approver.id)
  }
}

const orderLimitUserPatch = ref<OrderLimitUserPatch>(data())

// set correct data when dialog is reopened
watch(dialog, (newValue) => {
  if (newValue === true) {
    userApprovers.value = [...componentProps.user.approvers]
    orderLimitUserPatch.value = data()
  }
})

const save = () => {
  trackOrderLimitDialogue(componentProps.user.id, orderLimitUserPatch.value)
  if (orderLimitUserPatch.value.orderLimitType !== OrderLimitType.USER) {
    delete orderLimitUserPatch.value['userOrderLimit']
  }
  orderLimitUserPatch.value.approverIds = userApprovers.value.map((approver) => approver.id)
  useOrderLimits().updateOrderLimitUser(componentProps.user.id, orderLimitUserPatch.value)
  dialog.value = false
}

const closeDialog = () => {
  dialog.value = false
}
</script>

// Hack for firefox, as vuetify adds a label component that does grow automatically in chrome but
not in firefox.
<style scoped>
.individual-limit-radio :deep(label) {
  flex-grow: 1;
}
</style>
